import React, { useMemo, ReactElement } from "react";
import { cx, priceDisplay, useTranslations } from "@jugl-web/utils";
import { Plan, PlanTierIndex } from "@jugl-web/rest-api";
import { Tooltip } from "@jugl-web/ui-kit";
import { SubscriptionIntervalType } from "@jugl-web/rest-api/entities/models/common-types/SubscriptionIntervalType";
import {
  comparePlanTiers,
  getPlanTierIndex,
  PAID_PLAN_TIERS,
} from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { SelectedPlanInfo } from "@web-src/modules/cx/components/CompletePurchaseDialog/components/SummaryStep/types";
import { ReactComponent as EssentialsIcon } from "./assets/essentials.svg";
import { ReactComponent as AIAgentsIcon } from "./assets/ai-agents.svg";
import { ReactComponent as Integrations } from "./assets/integrations.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { PlanActionButton } from "../PlanActionButton";
import { DowngradeDisabledTooltip } from "../DowngradeDisabledTooltip";
import {
  getPlanSeatLimit,
  isDowngradeBlocked,
} from "../../utils/downgradeGuard";

// ---------- Constants ----------
const ADDON_ID_USERS = "jugl::users";
const ADDON_ID_AI_CREDITS = "jugl::ai_credits";
const ADDON_ID_KNOWLEDGE = "jugl::knowledge";
// Tickets currently reuse the `jugl::tasks` addon (kept in sync with `useTicketsLimit`).
const ADDON_ID_TICKETS = "jugl::tasks";

// ---------- Helpers ----------
const getAddonCredits = (plan: Plan, addonId: string): number | null =>
  plan.default_addons.find((a) => a.id === addonId)?.credits ?? null;

const formatNumber = (n: number): string => n.toLocaleString("en-US");

const allChecks = (count: number): React.ReactNode[] =>
  Array.from({ length: count }, (_, index) => <CheckIcon key={index} />);

const checksForTiers = (
  plans: Plan[],
  visibleOn?: readonly PlanTierIndex[]
): React.ReactNode[] =>
  plans.map((plan) =>
    visibleOn === undefined || visibleOn.includes(getPlanTierIndex(plan)) ? (
      <CheckIcon key={plan.id} />
    ) : null
  );

// ---------- Icon helpers ----------
const SectionIcon = ({ type }: { type: string }) => {
  const icons: Record<string, ReactElement> = {
    Essentials: <EssentialsIcon />,
    "AI Agent Features": <AIAgentsIcon />,
    "Chat Integrations": <Integrations />,
    Pricing: <PricingIcon />,
  };
  return icons[type] ?? null;
};

// ---------- Small UI pieces ----------
const CheckIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.5297 6.41002C19.8187 6.68324 19.9874 7.06005 19.9986 7.4576C20.0099 7.85515 19.8628 8.24089 19.5897 8.53002L11.0897 17.53C10.9519 17.6756 10.7864 17.7921 10.6028 17.8727C10.4193 17.9532 10.2215 17.9962 10.021 17.999C9.8206 18.0018 9.62165 17.9644 9.43592 17.889C9.2502 17.8136 9.08146 17.7017 8.93969 17.56L4.43969 13.06C4.17473 12.7757 4.03049 12.3996 4.03734 12.011C4.0442 11.6224 4.20162 11.2516 4.47645 10.9768C4.75128 10.7019 5.12205 10.5445 5.51065 10.5377C5.89925 10.5308 6.27534 10.6751 6.55969 10.94L9.96969 14.348L17.4097 6.47002C17.6829 6.18101 18.0597 6.01234 18.4573 6.00109C18.8548 5.98984 19.2406 6.13693 19.5297 6.41002Z"
      fill="#4F4F4F"
    />
  </svg>
);

const DiscountPill = ({ percent }: { percent: number }) => (
  <span className="flex h-[26px] items-center justify-center rounded-xl bg-[#3B71FE] px-3 text-[11px] font-bold text-white">
    {percent}% off
  </span>
);

// ---------- Types ----------
type ComparisonRowData = {
  label: string;
  values: React.ReactNode[];
};

type ComparisonSectionData = {
  title: string;
  rows: ComparisonRowData[];
};

// ---------- Reusable cell component ----------
const Cell = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cx(
      "flex items-center justify-center border-t-0 border-r-0 border-b border-l border-solid border-dark-200 px-6 py-6 text-center text-base text-dark-900",
      className
    )}
  >
    {children}
  </div>
);

// ---------- Header column component ----------
const PlanHeader = ({
  plan,
  allPlans,
  selectedInterval,
  isMostPopular,
  isDowngradeDisabled,
  onClick,
}: {
  plan: Plan;
  allPlans: Plan[];
  selectedInterval: SubscriptionIntervalType;
  isMostPopular?: boolean;
  isDowngradeDisabled?: boolean;
  onClick?: () => void;
}) => {
  const actionButton = (
    <PlanActionButton
      plan={plan}
      allPlans={allPlans}
      selectedInterval={selectedInterval}
      size="sm"
      isMostPopular={isMostPopular}
      className="w-[140px] whitespace-nowrap"
      isDisabled={isDowngradeDisabled}
      onClick={onClick}
    />
  );
  return (
    <div className="sticky top-0 z-[11] flex flex-col items-center space-y-4 border-t-0 border-r-0 border-b border-l border-solid border-dark-200 bg-white px-4 pb-8">
      <div className="absolute top-0 right-0 left-0 h-[1px]" />
      <span className="pt-4 font-bold text-[#1B1C1F]">{plan.name}</span>
      {isDowngradeDisabled ? (
        <Tooltip
          placement="top"
          className="max-w-[300px] rounded-[20px] p-4"
          renderTrigger={({ props, ref }) => (
            <div ref={ref} {...props}>
              {actionButton}
            </div>
          )}
        >
          <DowngradeDisabledTooltip
            planName={plan.name}
            seatLimit={getPlanSeatLimit(plan)}
          />
        </Tooltip>
      ) : (
        actionButton
      )}
    </div>
  );
};

// ---------- Feature row component ----------
const FeatureRow = ({
  row,
  planCount,
}: {
  row: ComparisonRowData;
  planCount: number;
}) => (
  <div
    className="grid transition-colors hover:bg-grey-100"
    style={{
      gridColumn: `1 / -1`,
      gridTemplateColumns: `1fr repeat(${planCount}, 1fr)`,
    }}
  >
    <div className="flex items-center border-t-0 border-r-0 border-b border-l-0 border-solid border-dark-200 px-6 py-6 text-base font-medium text-dark">
      {row.label}
    </div>
    {row.values.map((v, idx) => (
      <Cell key={+idx}>{v}</Cell>
    ))}
  </div>
);

export const PlanComparison: React.FC<{
  currentPlanId?: string;
  plans?: Plan[];
  selectedSubscriptionInterval?: SubscriptionIntervalType;
  entityUserCount?: number;
  isSubscriptionCanceled?: boolean;
  onSelectPlan?: (planInfo: SelectedPlanInfo) => void;
  onRequestDowngrade?: (args: {
    planInfo: SelectedPlanInfo;
    targetPlanName: string;
  }) => void;
  onPlanSelect?: (planId: string) => void;
}> = ({
  currentPlanId,
  plans,
  selectedSubscriptionInterval,
  entityUserCount = 0,
  isSubscriptionCanceled,
  onSelectPlan,
  onRequestDowngrade,
  onPlanSelect,
}) => {
  const { t } = useTranslations();
  const planCount = plans?.length ?? 0;
  const currentPlan = currentPlanId
    ? plans?.find((p) => p.id === currentPlanId)
    : undefined;

  const sections: ComparisonSectionData[] = useMemo(() => {
    if (!plans || plans.length === 0) return [];

    const mapAddon = (addonId: string, format: (credits: number) => string) =>
      plans.map((plan) => {
        const credits = getAddonCredits(plan, addonId);
        return credits === null ? "Unlimited" : format(credits);
      });

    return [
      {
        title: "Essentials",
        rows: [
          {
            label: "Workspace Seats",
            values: mapAddon(ADDON_ID_USERS, (c) => String(c)),
          },
          {
            label: "Max number of Seats",
            values: plans.map((_, i) => (i === 0 ? "1" : "Unlimited")),
          },
          {
            label: "Tickets",
            values: mapAddon(ADDON_ID_TICKETS, (c) => String(c)),
          },
          {
            label: "Customers management",
            values: allChecks(planCount),
          },
          { label: "Team management", values: allChecks(planCount) },
          { label: "Inbox", values: allChecks(planCount) },
          { label: "20+ Table Column types", values: allChecks(planCount) },
          { label: "iOS and Android Apps", values: allChecks(planCount) },
        ],
      },
      {
        title: "AI Agent Features",
        rows: [
          { label: "AI Agent setup", values: allChecks(planCount) },
          {
            label: "AI Agent Knowledge Size",
            values: mapAddon(ADDON_ID_KNOWLEDGE, (c) => `${c} MB`),
          },
          { label: "Playground", values: allChecks(planCount) },
          {
            label: "Message credits for AI Agent per month",
            values: mapAddon(ADDON_ID_AI_CREDITS, formatNumber),
          },
          { label: "Basic analytics", values: allChecks(planCount) },
          {
            label: "Advanced analytics",
            values: plans.map((plan, i) =>
              i === planCount - 1 ? <CheckIcon key={plan.id} /> : null
            ),
          },
          {
            label: "Intent classification",
            values: plans.map((_, i) => {
              const intentValues = [null, "3", "5", "Unlimited"];
              return intentValues[i] ?? null;
            }),
          },
        ],
      },
      {
        title: "Chat Integrations",
        rows: [
          { label: "WhatsApp", values: allChecks(planCount) },
          { label: "Instagram", values: allChecks(planCount) },
          { label: "Web Chat", values: allChecks(planCount) },
          { label: "SMS (Text) Chat", values: allChecks(planCount) },
          {
            label: t({
              id: "plan-comparison.live-chat-via-link",
              defaultMessage: "Live Chat via Link",
            }),
            values: allChecks(planCount),
          },
          {
            label: t({
              id: "plan-comparison.email",
              defaultMessage: "Email",
            }),
            values: allChecks(planCount),
          },
          {
            label: t({
              id: "plan-comparison.phone-calls",
              defaultMessage: "Phone Calls",
            }),
            values: checksForTiers(plans, PAID_PLAN_TIERS),
          },
        ],
      },
      {
        title: "Pricing",
        rows: [
          {
            label: "Billed Monthly",
            values: plans.map((plan) => {
              const monthlyPrice = plan.prices.find(
                (p) => p.interval === "month"
              );
              if (!monthlyPrice || monthlyPrice.unit_amount === 0)
                return "$0 forever";
              return (
                <span key={plan.id}>
                  {priceDisplay(
                    monthlyPrice.unit_amount,
                    monthlyPrice.currency,
                    false,
                    { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                  )}{" "}
                  per month
                </span>
              );
            }),
          },
          {
            label: "Billed Annual",
            values: plans.map((plan) => {
              const yearlyPrice = plan.prices.find(
                (p) => p.interval === "year"
              );
              const monthlyPrice = plan.prices.find(
                (p) => p.interval === "month"
              );
              if (
                !yearlyPrice ||
                yearlyPrice.unit_amount === 0 ||
                !monthlyPrice
              )
                return "$0 forever";

              const monthlyEquivalent = Math.round(
                yearlyPrice.unit_amount / 12
              );
              const monthlyTotal = monthlyPrice.unit_amount * 12;
              const discount = Math.round(
                ((monthlyTotal - yearlyPrice.unit_amount) / monthlyTotal) * 100
              );

              return (
                <span
                  key={plan.id}
                  className="flex flex-wrap items-center justify-center gap-3"
                >
                  <span>
                    {priceDisplay(
                      monthlyEquivalent,
                      yearlyPrice.currency,
                      false,
                      { minimumFractionDigits: 0, maximumFractionDigits: 0 }
                    )}{" "}
                    per month
                  </span>
                  {discount > 0 && <DiscountPill percent={discount} />}
                </span>
              );
            }),
          },
        ],
      },
    ];
  }, [plans, planCount, t]);

  const handlePlanClick = (planIndex: number) => {
    const plan = plans?.[planIndex];
    if (!plan || !selectedSubscriptionInterval) return;

    const isCurrentPlan = plan.id === currentPlanId;
    if (isCurrentPlan) return;

    if (
      isDowngradeBlocked({
        plan,
        currentPlan,
        entityUserCount,
        isSubscriptionCanceled,
      })
    ) {
      return;
    }

    onPlanSelect?.(plan.id);

    const planComparisonResult =
      currentPlan && !isCurrentPlan
        ? comparePlanTiers(currentPlan, plan)
        : undefined;

    if (planComparisonResult === "downgrade") {
      onRequestDowngrade?.({
        planInfo: {
          planId: plan.id,
          period: selectedSubscriptionInterval,
        },
        targetPlanName: plan.name,
      });
    } else {
      onSelectPlan?.({
        planId: plan.id,
        period: selectedSubscriptionInterval,
      });
    }
  };

  return (
    <div className="flex w-full flex-col px-4 pt-20 pb-20 font-secondary md:px-10">
      <div className="mb-2 text-center font-primary text-[48px] font-semibold text-black">
        Complete features list
      </div>
      <div className="pb-10 text-center font-primary text-base text-dark-700">
        Compare all features, limits, and extras across all plans
      </div>
      <div className="jugl__custom-scrollbar w-full overflow-x-auto">
        <div className="min-w-[1000px] px-4 md:px-10">
          <div
            className="grid"
            style={{ gridTemplateColumns: `1fr repeat(${planCount}, 1fr)` }}
          >
            {/* Empty top-left cell */}
            <div className="sticky top-0 z-[11] border-t-0 border-r-0 border-b border-l-0 border-solid border-dark-200 bg-white">
              <div className="absolute top-0 right-0 left-0 h-[1px]" />
            </div>
            {/* Plan headers */}
            {plans?.map((plan, index) => (
              <PlanHeader
                key={plan.id}
                plan={plan}
                allPlans={plans}
                // oxlint-disable-next-line typescript/no-non-null-assertion
                selectedInterval={selectedSubscriptionInterval!}
                isMostPopular={index === 2}
                isDowngradeDisabled={isDowngradeBlocked({
                  plan,
                  currentPlan,
                  entityUserCount,
                  isSubscriptionCanceled,
                })}
                onClick={() => handlePlanClick(index)}
              />
            ))}
            {/* Section titles and rows */}
            {sections.map((section, index) => (
              <React.Fragment key={section.title}>
                <div
                  className={cx(
                    "flex items-center gap-2.5 py-6 text-[22px] font-bold text-dark",
                    { "pt-[56px]": index > 0 }
                  )}
                  style={{ gridColumn: `1 / -1` }}
                >
                  <SectionIcon type={section.title} />
                  {section.title}
                </div>
                {section.rows.map((row) => (
                  <FeatureRow key={row.label} row={row} planCount={planCount} />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
